/**
 * OAuth2 PKCE authentication strategy.
 *
 * Implements the Authorization Code Flow with PKCE (RFC 7636) for browser
 * SPAs. Browser authorization is a top-level navigation; all IDAAS API calls
 * use the generated `@zero/idaas-api` client.
 */

import { createClient, oidcProtocolExchangeToken } from "@zero/idaas-api";
import {
  buildAuthorizeUrl,
  clearPkceState,
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
  OAuth2CallbackInputError,
  parseCallbackParams,
  retrievePkceState,
  storePkceState,
} from "../../oauth2";
import { taggedAuthError } from "../auth-error";
import { loadRuntimeConfig } from "../runtime-config";
import type { AuthResult, AuthStrategy, OAuth2PKCEConfig, StartLoginOptions, TokenSet } from "../types";
import { resolveRequestTimeout, validateOAuth2PKCEConfig } from "./config-validation";
import {
  createTokenValidationContext,
  exchangeAuthorizationCode,
  fetchUserInfo,
  logoutRemote,
  resolveClientInfo,
  tokenResponseToTokenSet,
  tokenSetToAuthResult,
  toProviderRefreshError,
  validateTokenSet,
} from "./oidc-token-helpers";

const DEFAULT_SCOPE = "openid offline_access";

/**
 * Create an OAuth2 PKCE authentication strategy.
 *
 * ```typescript
 * const strategy = createOAuth2PKCEProvider({
 *   kind: "oauth2-pkce",
 *   idaasBaseUrl: "http://localhost:50051",
 *   clientId: "my-app",
 *   redirectUri: "http://localhost:3000/auth/callback",
 * });
 * ```
 */
export function createOAuth2PKCEProvider(config: OAuth2PKCEConfig): AuthStrategy {
  validateOAuth2PKCEConfig(config);

  const idaasBaseUrl = config.idaasBaseUrl;
  const clientId = config.clientId;
  const resource = config.resource;
  const redirectUri = config.redirectUri;
  const scope = config.scope ?? DEFAULT_SCOPE;
  const pkceNamespace = { idaasBaseUrl, clientId, resource };
  // Runtime issuer channel (99 D31): deployed surfaces serve /.zero/runtime.json
  // carrying the environment's central issuer — never baked into the bundle.
  // An explicitly configured issuer (local dev override) wins and skips the
  // fetch; a missing channel keeps the idaasBaseUrl-origin fallback (vite dev).
  let tokenValidationContext = createTokenValidationContext(idaasBaseUrl, clientId, resource, config.issuer);
  const issuerReady = config.issuer
    ? undefined
    : loadRuntimeConfig().then((runtime) => {
        if (runtime.idaasIssuer === undefined) return;
        tokenValidationContext = createTokenValidationContext(idaasBaseUrl, clientId, resource, runtime.idaasIssuer);
      });
  const validationContext = async (): Promise<typeof tokenValidationContext> => {
    await issuerReady;
    return tokenValidationContext;
  };
  const idaasClient = createClient({ baseURL: idaasBaseUrl, timeout: resolveRequestTimeout(config.requestTimeoutMs) });
  return {
    kind: "oauth2-pkce",

    async logout(tokenSet: TokenSet | null) {
      return logoutRemote(idaasClient, clientId, tokenSet);
    },

    async startLogin(options?: StartLoginOptions): Promise<undefined> {
      if (options && options.mode !== undefined && options.mode !== "redirect") {
        throw taggedAuthError({
          kind: "unsupported_mode",
          message: `OAuth2PKCEProvider does not support startLogin mode "${options.mode}"`,
        });
      }

      const doRedirect = async () => {
        const verifier = generateCodeVerifier();
        const state = generateState();

        const challenge = await generateCodeChallenge(verifier);

        await resolveClientInfo(idaasClient, clientId, redirectUri);

        const url = buildAuthorizeUrl({
          idaasBaseUrl,
          clientId,
          resource,
          redirectUri,
          scope,
          codeChallenge: challenge,
          state,
        });

        storePkceState(pkceNamespace, verifier, state, window.location.pathname);
        try {
          window.location.href = url;
        } catch (error) {
          clearPkceState(pkceNamespace, state);
          throw error;
        }
      };

      await doRedirect();
      return undefined;
    },

    async handleCallback(): Promise<AuthResult> {
      let params: ReturnType<typeof parseCallbackParams>;
      try {
        params = parseCallbackParams();
      } catch (error) {
        if (error instanceof OAuth2CallbackInputError) {
          throw taggedAuthError({
            kind: error.field === "state" ? "invalid_state" : "invalid_provider_response",
            message: error.message,
          });
        }
        throw error;
      }
      if (!params) {
        throw new Error("No OAuth2 callback parameters found in URL");
      }

      const pkceState = retrievePkceState(pkceNamespace, params.state);
      if (!pkceState) {
        throw new Error("No PKCE state found. The login session may have expired.");
      }

      if (params.state !== pkceState.state) {
        throw new Error("OAuth2 state mismatch. Possible CSRF attack.");
      }

      const tokenResponse = await exchangeAuthorizationCode(idaasClient, {
        clientId,
        redirectUri,
        code: params.code,
        codeVerifier: pkceState.verifier,
      });
      const tokenSet = tokenResponseToTokenSet(tokenResponse, await validationContext());
      const userInfo = await fetchUserInfo(idaasClient, tokenSet.accessToken);

      return tokenSetToAuthResult(tokenSet, userInfo);
    },

    async refreshToken(refreshToken: string): Promise<TokenSet> {
      const response = await oidcProtocolExchangeToken({
        client: idaasClient,
        throwOnError: true,
        body: {
          grant_type: "refresh_token",
          refresh_token: refreshToken,
          client_id: clientId,
        },
      }).catch((error: unknown) => Promise.reject(toProviderRefreshError(error)));
      return tokenResponseToTokenSet(response.data, await validationContext());
    },

    supportsRedirect(): boolean {
      return true;
    },

    supportsPopup(): boolean {
      return false;
    },

    supportsIframe(): boolean {
      return false;
    },

    validateTokenSet(tokenSet: TokenSet, options?: { allowExpired?: boolean }): TokenSet {
      return validateTokenSet(tokenSet, tokenValidationContext, options);
    },
  };
}
